import { DcrKeyPipe } from '@blk/cwp-ng-framework';
import { FundStatsConfigKey, InjectConfig, IqConfigKey } from '@ddt-web/config-layer';
import { ComponentData, TabData, TabUtil } from '@ddt-web/data-service';
import { PasAsset } from '@ddt-web/pas';
import * as partition from 'lodash/partition';
import * as snakeCase from 'lodash/snakeCase';
import * as toUpper from 'lodash/toUpper';

import { DcrConstants } from '../constants/dcr.constants';
import { GranularStatusChange } from '../enums';
import { AreaOfFocusCardData } from '../models/area-of-focus.interface';
import { AggregatedStatusChangeCountByStatusChangeType, FundDimension, FundHealthStatusCardData, FundStats, FundStatsItem, FundStatus, FundStatusChange, GranularStatusChangeByStatusChangeType } from '../models/fund-stats.interface';
import { AggregatedStatusInteractionDataByStatus } from '../models/fund-status-interaction.interface';
import { StatusColour } from '../models/status-colour.interface';

export class FundStatsUtil {
	@InjectConfig(FundStatsConfigKey.STATUSES) private static readonly statuses: FundStatus[] = [];
	@InjectConfig(FundStatsConfigKey.DIMENSIONS) private static readonly dimensions: FundDimension[] = [];
	@InjectConfig(IqConfigKey.ENABLE_FUND_STATUS_INTERACTIONS) private static readonly enableStatusInteractions = false;

	public static createStatusCards(data: ComponentData, isDataLoading: boolean = false): Array<FundHealthStatusCardData> {
		let statusCards = [];

		if (TabUtil.isDataAvailable(data, 'fund.statuses') || isDataLoading) {
			const cells = data?.getTabById('fund.statuses')?.tableData?.[0]?.cells?.[0] || [];
			const statusInteractions: AggregatedStatusInteractionDataByStatus = data?.getTabById('fund.status.interactions')?.tableData[0].cells[0][0]
				.formattedValue as unknown as AggregatedStatusInteractionDataByStatus;
			const dcrKeyPipe = new DcrKeyPipe();

			statusCards = this.statuses.map(status => {
				const statusColour = StatusColour.get[status.statusId];
				const dismissCount = statusInteractions?.[statusColour.colour]?.dismiss ?? 0;
				const acceptCount = statusInteractions?.[statusColour.colour]?.accept ?? 0;
				const totalFunds = +cells.find(cell => cell.config.id === status.statusId)?.rawValue;

				return {
					title: {
						dcrKey: dcrKeyPipe.transform('FH_STATUS_' + status.statusId + '_TITLE'),
						dcrValue: status.description
					},
					subtitle: {
						dcrKey: dcrKeyPipe.transform('FH_STATUS_' + status.statusId + '_SUBTITLE'),
						dcrValue: DcrConstants[dcrKeyPipe.transform('FH_STATUS_' + status.statusId + '_SUBTITLE')]
					},
					buttonText: {
						dcrKey: dcrKeyPipe.transform('FH_STATUS_' + status.statusId + '_BUTTON_TEXT'),
						dcrValue: DcrConstants[dcrKeyPipe.transform('FH_STATUS_' + status.statusId + '_BUTTON_TEXT')]
					},
					tooltipDcr: dcrKeyPipe.transform('FH_STATUS_' + status.statusId + '_TOOLTIP'),
					colour: statusColour,
					statusIdToShow: status.statusIdToShow,
					totalFunds: this.enableStatusInteractions ? totalFunds - dismissCount : totalFunds,
					acceptCount: acceptCount,
					dismissCount: dismissCount,
					unreviewedCount: this.enableStatusInteractions ? totalFunds - acceptCount - dismissCount : 0
				};
			});
		}
		return statusCards;
	}

	public static createAreaOfFocusCardData(tabData: TabData, status: FundStatus): Array<AreaOfFocusCardData> {
		const isValidData = !!tabData?.tableData[0]?.cells?.[0]?.filter(data => !!data.rawValue && data.rawValue['count'] !== 0)?.length;
		if (isValidData && status && this.dimensions?.length) {
			return tabData.tableData[0].cells[0]
				.map((cell: { config: any; rawValue: any }) => {
					const aof = status?.aofs?.find(aof => aof.aofId === cell.config.id);
					return {
						title: aof?.description,
						dimension: this.dimensions.find(dimension => dimension.dimensionId === aof?.dimension)?.description,
						fundCount: Number(cell.rawValue.count),
						link: aof?.aofIdToShow,
						dcrKey: toUpper(snakeCase(aof?.aofId))
					};
				})
				.sort((a, b) => b.fundCount - a.fundCount);
		} else {
			return [];
		}
	}

	public static calculateFundStatus(...dataSources: PasAsset[]): StatusColour {
		const statuses = dataSources.map(holding => {
			if (holding?.customClientProperties) {
				const redStatus = this.statuses.find(status => status.statusId === 'red');
				const yellowStatus = this.statuses.find(status => status.statusId === 'yellow');
				const custom = holding.customClientProperties;

				const isRed = redStatus.aofs.some(redAof => redAof.subAofIds.some(subAofId => custom[subAofId] === 'true'));
				const isYellow = !isRed && yellowStatus.aofs.some(yellowAof => yellowAof.subAofIds.some(subAofId => custom[subAofId] === 'true'));

				if (isRed) {
					return StatusColour.RED;
				} else if (isYellow) {
					return StatusColour.YELLOW;
				}
				return StatusColour.GREEN;
			} else {
				return null;
			}
		});

		if (statuses.length === 1) {
			return statuses[0];
		}

		return FundStatsUtil.getPrimaryStatus(statuses);
	}

	public static checkAofColour(holding: PasAsset, aofId: string): string {
		const statuses = this.statuses.filter(status => !!status.aofs?.find(aof => aof.aofId === aofId));
		const matchingAofs = statuses.map(status => {
			return status?.aofs.find(aof => aof.aofId === aofId);
		});
		const matchingStatusColours = matchingAofs
			.map((aof, i) => {
				if (aof?.subAofIds.some(subAofId => holding?.customClientProperties?.[subAofId] === 'true')) {
					return this.translateFundStatusToString(statuses[i]);
				}
			})
			.filter(statusColour => !!statusColour);

		if (matchingStatusColours.length) {
			return FundStatsUtil.getPrimaryStatus(matchingStatusColours);
		}

		return '';
	}

	public static checkSubAofColour(holding: PasAsset, subAofId: string): string {
		if (holding?.customClientProperties?.[subAofId] === 'true') {
			const status = this.statuses.find(status => !!status.aofs?.find(aof => aof.subAofIds.includes(subAofId)));
			if (!!status) {
				return this.translateFundStatusToString(status);
			}
		}
		return '';
	}

	public static fundStatsToItemCount(statusesOrAofCategory: { [statusOrAof: string]: FundStatsItem }): { [statusOrAof: string]: number } {
		return Object.entries(statusesOrAofCategory).reduce((acc, [statusOrAof, val]: [string, FundStatsItem]) => {
			acc[statusOrAof] = val.count;
			return acc;
		}, {});
	}

	/**
	 * Method to sort the status changes of a fund based on the sorting logic as statusChangesToSort
	 *
	 * @param funds - the content of the FundStatusChange
	 * @returns - a sorted array with status and count of the affected funds
	 */
	public static sortStatusChanges(funds: FundStatusChange): [string, number][] {
		const statusChangesToSort = [GranularStatusChange.GREEN_YELLOW, GranularStatusChange.YELLOW_RED, GranularStatusChange.GREEN_RED];
		return Object.entries(funds).sort(
			(value1: [GranularStatusChange, number], value2: [GranularStatusChange, number]) => statusChangesToSort.indexOf(value2[0]) - statusChangesToSort.indexOf(value1[0])
		);
	}

	/**
	 * Method to calculate the number of funds which have been upgraded of downgraded based on the fund requestIds from in the fundStats and fundStatsCompare stores
	 * Funds can exist in both red and yellow aofs in the fundStats. In this case, the fund status is considered to be red
	 *
	 * @param fundStats - the content of the FundStats store
	 * @param fundStatsCompare - the content of the FundStatsCompare store
	 * @returns an object containing the number of funds which have been upgraded or downgraded
	 */
	public static countUpgradedAndDowngradedFunds(
		fundStats: FundStats,
		fundStatsCompare: FundStats,
		isAggregated: boolean = true
	): AggregatedStatusChangeCountByStatusChangeType | GranularStatusChangeByStatusChangeType {
		const [allFunds, redAofFunds, yellowAofFunds] = this.getUniqueIdsForFundStats(fundStats);
		const [allFundsCompare, redAofFundsCompare, yellowAofFundsCompare] = this.getUniqueIdsForFundStats(fundStatsCompare);

		const downgradedFundsRed = Array.from(redAofFunds).filter(mstarSecId => {
			if (!redAofFundsCompare.has(mstarSecId) && (yellowAofFundsCompare.has(mstarSecId) || allFundsCompare.has(mstarSecId))) {
				// if was yellow or green previously
				return true;
			} else {
				// when there was no historical data
				return false;
			}
		});
		const downgradedFundsYellow = Array.from(yellowAofFunds).filter(mstarSecId => {
			if (redAofFundsCompare.has(mstarSecId) || yellowAofFundsCompare.has(mstarSecId)) {
				// if was red or yellow previously
				return false;
			} else if (allFundsCompare.has(mstarSecId)) {
				// if was green previously
				return true;
			} else {
				// when there was no historical data
				return false;
			}
		});
		const downgradedFunds = [...downgradedFundsRed, ...downgradedFundsYellow];

		const upgradedFundsYellow = Array.from(yellowAofFunds).filter(mstarSecId => {
			if (redAofFundsCompare.has(mstarSecId)) {
				// if was red previously
				return true;
			} else {
				// previously green or no data
				return false;
			}
		});

		const upgradedFundsGreen = Array.from(allFunds).filter(mstarSecId => {
			// we must filter allFunds as there is no green status in fundStats
			if (!redAofFunds.has(mstarSecId) && !yellowAofFunds.has(mstarSecId) && allFundsCompare.has(mstarSecId)) {
				// check the fund has historical data
				if (redAofFundsCompare.has(mstarSecId) || yellowAofFundsCompare.has(mstarSecId)) {
					// if it was previously red or yellow
					return true;
				} else {
					return false; // if it was previously green
				}
			} else {
				return false;
			}
		});
		const upgradedFunds = [...upgradedFundsYellow, ...upgradedFundsGreen];

		if (!isAggregated) {
			const [yellowToRed, greenToRed] = partition(downgradedFundsRed, requestId => yellowAofFundsCompare.has(requestId));
			const [redToGreen, yellowToGreen] = partition(upgradedFundsGreen, requestId => redAofFundsCompare.has(requestId));

			if (!downgradedFunds.length && !upgradedFunds.length) {
				return null;
			} else {
				return {
					downgradedFunds: {
						yellowRed: yellowToRed.length,
						greenRed: greenToRed.length,
						greenYellow: downgradedFundsYellow.length
					},
					upgradedFunds: {
						yellowRed: upgradedFundsYellow.length,
						greenRed: redToGreen.length,
						greenYellow: yellowToGreen.length
					}
				};
			}
		} else {
			return { upgraded: upgradedFunds.length, downgraded: downgradedFunds.length };
		}
	}

	public static getPrimaryStatus(statuses: StatusColour[]): StatusColour;
	public static getPrimaryStatus(statuses: string[]): string;
	public static getPrimaryStatus(statuses: (string | StatusColour)[]) {
		const sortedStatuses = statuses
			.filter(status => !!status)
			.sort((a, b) => {
				return StatusColour.STATUS_SORT_ORDER.get(a instanceof StatusColour ? a.colour : a) - StatusColour.STATUS_SORT_ORDER.get(b instanceof StatusColour ? b.colour : b);
			});

		return sortedStatuses[0];
	}

	private static translateFundStatusToString(status: FundStatus): string {
		return status?.statusId === 'red' ? StatusColour.RED.colour : StatusColour.YELLOW.colour;
	}

	private static getUniqueIdsForFundStats(fundStats: FundStats): [allFunds: Set<string>, redAofFunds: Set<string>, yellowAofFunds: Set<string>] {
		const allFunds = this.getUniqueRequestIdsFromAof(fundStats.status);
		const redAofFunds = this.getUniqueRequestIdsFromAof(fundStats.redAof);
		const yellowAofFunds = this.getUniqueRequestIdsFromAof(fundStats.yellowAof, new Set([...Array.from(redAofFunds)]));

		return [allFunds, redAofFunds, yellowAofFunds];
	}

	private static getUniqueRequestIdsFromAof(fundStatsItem: { [statusOrAof: string]: FundStatsItem }, filterSet?: Set<string>): Set<string> {
		const requestIds = new Set<string>();
		if (!fundStatsItem) {
			return requestIds;
		} else {
			return Object.values(fundStatsItem).reduce((acc, status) => {
				status.holdings.filter(holding => !filterSet?.has(holding.morningstarSecId)).forEach(holding => acc.add(holding.morningstarSecId));
				return acc;
			}, requestIds);
		}
	}
}
